import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, FileText, Trash2, Download, Check, MessageSquare,
  CreditCard, TrendingUp, Image as ImageIcon, Sparkles, Wand2, Eye, Loader2,
  Send, Sparkle, MessagesSquare, Paperclip, Lock, ShieldCheck, Plus,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Timeline } from "@/components/Timeline";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RoyaltySection } from "@/components/RoyaltySection";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "adesso";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} giorni fa`;
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
};

const COMPARE_KEYS = ["essential_onetime", "professional_onetime"];
const DOC_FOLDER_LABELS = { copertine: "Copertine", capitoli: "Bozze capitoli", altro: "Altro" };

// Uploaded covers are stored as files: image_url is a "cover-file:{id}" marker resolved to the image endpoint
const coverSrc = (url) => (url?.startsWith("cover-file:") ? `${API}/covers/${url.split(":")[1]}/image` : url);
const COMPARE_ROWS = [
  { label: "Copertina professionale", vals: [true, true] },
  { label: "Editing del libro (fino a ~100 pagine)", vals: [true, true] },
  { label: "Copertina premium personalizzata", vals: [false, true] },
  { label: "Impaginazione avanzata", vals: [false, true] },
  { label: "Gestione vendite su Amazon (50%)", vals: [false, true] },
  { label: "Rendiconto e pagamenti ogni 2 mesi", vals: [false, true] },
  { label: "Monitoraggio e condivisione recensioni", vals: [false, true] },
  { label: "Inviti dei nostri lettori a recensire", vals: [false, true] },
];

export default function BookDetail() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [book, setBook] = useState(null);
  const [states, setStates] = useState([]);
  const [gateStates, setGateStates] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [covers, setCovers] = useState([]);
  const [royalty, setRoyalty] = useState({
    author_share_pct: 50,
    kindle: { entries: [], totals: { units: 0, gross: 0, author_share: 0, paid_to_author: 0 } },
    paperback: { entries: [], totals: { units: 0, gross: 0, author_share: 0, paid_to_author: 0 } },
    legacy: [],
  });
  const [packages, setPackages] = useState([]);
  const fileRef = useRef(null);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [docFolder, setDocFolder] = useState("altro");
  const [docFeedbackFor, setDocFeedbackFor] = useState(null);
  const [docFeedbackText, setDocFeedbackText] = useState("");
  const [coverLabel, setCoverLabel] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const coverFileRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);
  const [readByAuthor, setReadByAuthor] = useState({ chat_read_at: null, updates_read_at: null });
  const [recommendation, setRecommendation] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return ["updates", "chat", "documents", "chapters", "cover", "royalty", "payment"].includes(t) ? t : "updates";
  });
  const chatEndRef = useRef(null);
  const chatFileRef = useRef(null);
  const [unread, setUnread] = useState({ chat: 0, updates: 0 });
  const [attUrls, setAttUrls] = useState({});
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidPkg, setMarkPaidPkg] = useState("professional_onetime");
  const [markPaidBusy, setMarkPaidBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [b, st, up, dc, ch, cv, ro, pk, ms, rec, un] = await Promise.all([
        api.get(`/books/${bookId}`),
        api.get(`/config/timeline`),
        api.get(`/books/${bookId}/updates`),
        api.get(`/books/${bookId}/documents`),
        api.get(`/books/${bookId}/chapters`),
        api.get(`/books/${bookId}/covers`),
        api.get(`/books/${bookId}/royalties`),
        api.get(`/config/packages`),
        api.get(`/books/${bookId}/messages`),
        api.get(`/books/${bookId}/recommendation`),
        api.get(`/unread`),
      ]);
      setBook(b.data);
      setStates(st.data.states);
      setGateStates(st.data.gate_states || []);
      setUpdates(up.data.updates);
      setDocs(dc.data.documents);
      setChapters(ch.data.chapters);
      setCovers(cv.data.covers);
      setRoyalty(ro.data);
      setPackages(pk.data.packages);
      setMessages(ms.data.messages);
      setReadByAuthor({
        chat_read_at: ms.data.author_chat_read_at || null,
        updates_read_at: ms.data.author_updates_read_at || null,
      });
      setRecommendation(rec.data);
      setUnread(un.data.books?.[bookId] || { chat: 0, updates: 0 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/dashboard");
    }
  }, [bookId, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Live chat polling while the Chat tab is open
  useEffect(() => {
    if (activeTab !== "chat") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/books/${bookId}/messages`);
        setMessages(data.messages);
        setReadByAuthor({
          chat_read_at: data.author_chat_read_at || null,
          updates_read_at: data.author_updates_read_at || null,
        });
      } catch {
        /* ignore */
      }
    }, 4000);
    return () => clearInterval(id);
  }, [activeTab, bookId]);

  useEffect(() => {
    if (activeTab === "chat") chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeTab]);

  // Lazily fetch image attachments (cached by id) so chat polling doesn't refetch
  useEffect(() => {
    messages.forEach(async (m) => {
      const a = m.attachment;
      if (a?.is_image && !attUrls[a.attachment_id]) {
        try {
          const { data } = await api.get(`/attachments/${a.attachment_id}`);
          setAttUrls((p) => ({ ...p, [a.attachment_id]: `data:${data.content_type};base64,${data.data}` }));
        } catch {
          /* ignore */
        }
      }
    });
  }, [messages, attUrls]);

  const handleTabChange = (v) => {
    setActiveTab(v);
    if (v === "chat" && unread.chat) {
      api.post(`/books/${bookId}/read`, { section: "chat" }).catch(() => {});
      setUnread((u) => ({ ...u, chat: 0 }));
    }
    if (v === "updates" && unread.updates) {
      api.post(`/books/${bookId}/read`, { section: "updates" }).catch(() => {});
      setUnread((u) => ({ ...u, updates: 0 }));
    }
  };

  const uploadChatAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAtt(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("text", msgInput.trim());
    setMsgInput("");
    try {
      const { data } = await api.post(`/books/${bookId}/messages/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingAtt(false);
      if (chatFileRef.current) chatFileRef.current.value = "";
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const { data } = await api.get(`/attachments/${att.attachment_id}`);
      const link = document.createElement("a");
      link.href = `data:${data.content_type};base64,${data.data}`;
      link.download = att.filename;
      link.click();
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const changeStatus = async (status) => {
    if (!isAdmin) return;
    try {
      const { data } = await api.patch(`/books/${bookId}`, { status });
      setBook(data);
      toast.success(`Stato aggiornato: ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markPaid = async () => {
    if (!isAdmin) return;
    setMarkPaidBusy(true);
    try {
      const { data } = await api.post(`/books/${bookId}/admin/mark-paid`, { lookup_key: markPaidPkg });
      setBook(data);
      setMarkPaidOpen(false);
      toast.success("Libro attivato come pagato. Produzione avviata.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMarkPaidBusy(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", docFolder);
    try {
      await api.post(`/books/${bookId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato: in attesa di doppia approvazione");
      const dc = await api.get(`/books/${bookId}/documents`);
      setDocs(dc.data.documents);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const approveDoc = async (docId) => {
    try {
      const { data } = await api.post(`/documents/${docId}/approve`);
      toast.success(data.status === "fully_approved" ? "File approvato da entrambi" : "Approvazione registrata: in attesa dell'altra parte");
      const dc = await api.get(`/books/${bookId}/documents`);
      setDocs(dc.data.documents);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submitDocFeedback = async () => {
    try {
      await api.post(`/documents/${docFeedbackFor}/request-changes`, { feedback: docFeedbackText });
      toast.success("Richiesta di modifica inviata: le approvazioni sono state azzerate");
      setDocFeedbackFor(null);
      setDocFeedbackText("");
      const dc = await api.get(`/books/${bookId}/documents`);
      setDocs(dc.data.documents);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const { data } = await api.get(`/documents/${docId}`);
      const link = document.createElement("a");
      link.href = `data:${data.content_type};base64,${data.data}`;
      link.download = filename;
      link.click();
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      setDocs((d) => d.filter((x) => x.doc_id !== docId));
      toast.success("Documento eliminato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const approveChapter = async (chId) => {
    await api.post(`/chapters/${chId}/approve`);
    toast.success("Capitolo approvato");
    const ch = await api.get(`/books/${bookId}/chapters`);
    setChapters(ch.data.chapters);
  };

  const submitFeedback = async () => {
    await api.post(`/chapters/${feedbackFor}/request-changes`, { feedback: feedbackText });
    toast.success("Richiesta di modifica inviata");
    setFeedbackFor(null);
    setFeedbackText("");
    const ch = await api.get(`/books/${bookId}/chapters`);
    setChapters(ch.data.chapters);
  };

  const selectCover = async (url) => {
    await api.post(`/books/${bookId}/select-cover`, { image_url: url });
    setBook((b) => ({ ...b, selected_cover: url }));
    toast.success("Copertina selezionata");
  };

  const uploadCover = async () => {
    if (!coverFile) return toast.error("Scegli un'immagine di copertina");
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", coverFile);
      fd.append("label", coverLabel);
      await api.post(`/books/${bookId}/covers/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Copertina caricata tra le proposte");
      setCoverLabel("");
      setCoverFile(null);
      if (coverFileRef.current) coverFileRef.current.value = "";
      const cv = await api.get(`/books/${bookId}/covers`);
      setCovers(cv.data.covers);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCoverBusy(false);
    }
  };

  const deleteCover = async (coverId) => {
    try {
      await api.delete(`/covers/${coverId}`);
      toast.success("Copertina rimossa");
      const cv = await api.get(`/books/${bookId}/covers`);
      setCovers(cv.data.covers);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const checkout = async (lookupKey) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        book_id: bookId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const generateDraft = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/books/${bookId}/generate-draft`);
      toast.success(`Struttura pronta! ${data.chapters_created} capitoli generati.`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const openPreview = () => window.open(`/preview/${bookId}`, "_blank");

  const downloadQuestionnaire = async () => {
    try {
      const res = await api.get(`/books/${bookId}/questionnaire/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `questionario-${book?.title || "libro"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Questionario scaricato in PDF");
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const exportChat = async () => {
    try {
      const res = await api.get(`/books/${bookId}/chat/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `chat-${book?.title || "libro"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Conversazione esportata in PDF");
    } catch {
      toast.error("Esportazione non riuscita");
    }
  };

  const sendChatMessage = async (e) => {
    e?.preventDefault();
    if (!msgInput.trim()) return;
    setSending(true);
    const text = msgInput.trim();
    setMsgInput("");
    try {
      const { data } = await api.post(`/books/${bookId}/messages`, { text });
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setMsgInput(text);
    } finally {
      setSending(false);
    }
  };

  if (!book) {
    return (
      <DashboardLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#D4AF37]" />
        </div>
      </DashboardLayout>
    );
  }

  const chapterStatusLabel = {
    approved: { t: "Approvato", c: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" },
    pending_approval: { t: "Da approvare", c: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10" },
    changes_requested: { t: "Modifiche richieste", c: "text-orange-400 border-orange-400/30 bg-orange-400/10" },
  };

  // Prezzi di lancio terminati: si vende solo il pacchetto full (Professional €497).
  // Essential resta visibile solo ai libri legacy già registrati con quel pacchetto.
  const visiblePackages = packages.filter(
    (p) => p.lookup_key === "professional_onetime" || book.package === p.lookup_key
  );

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl">
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="back-to-dashboard-btn"
          className="mb-8 flex items-center gap-2 text-sm text-white/40 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Tutti i libri
        </button>

        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div className="flex flex-col gap-2">
            <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#D4AF37] w-fit">
              {book.status}
            </span>
            <h1 className="mt-2 font-serif text-5xl font-light text-white" data-testid="book-title">{book.title}</h1>
            {book.subtitle && <p className="text-lg text-white/40">{book.subtitle}</p>}
            {book.genre && <p className="text-sm text-white/30">{book.genre}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && !book.package && (
              <Button
                onClick={() => setMarkPaidOpen(true)}
                data-testid="admin-mark-paid-btn"
                className="gap-2 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/25"
              >
                <ShieldCheck className="h-4 w-4" /> Segna come pagato
              </Button>
            )}
            {isAdmin && book.package && (
              <span data-testid="admin-book-paid-badge" className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-xs text-[#D4AF37]">
                Pacchetto attivo: {book.package === "professional_onetime" ? "Professional" : "Essential"}
              </span>
            )}
            <Button
              onClick={downloadQuestionnaire}
              data-testid="download-questionnaire-btn"
              variant="outline"
              className="gap-2 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5"
            >
              <Download className="h-4 w-4" /> Scarica questionario
            </Button>
            <Button
              onClick={openPreview}
              data-testid="preview-book-btn"
              variant="outline"
              className="gap-2 rounded-full border-[#D4AF37]/30 bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              <Eye className="h-4 w-4" /> Anteprima libro
            </Button>
          </div>
        </div>

        {!book.package && chapters.some((c) => c.status === "approved") && (
          <div className="mt-10 flex flex-col gap-4 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/10 to-transparent p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="payment-gate-banner">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/15 text-[#D4AF37]"><Lock className="h-5 w-5" /></span>
              <div>
                <p className="font-serif text-lg text-white">Hai approvato i capitoli!</p>
                <p className="text-sm text-white/60">
                  {isAdmin
                    ? "La produzione partirà solo dopo che l'autore avrà scelto e pagato un pacchetto."
                    : "Scegli un pacchetto e completa il pagamento per avviare la creazione del tuo libro."}
                </p>
              </div>
            </div>
            {!isAdmin && (
              <Button onClick={() => setActiveTab("payment")} data-testid="gate-choose-package-btn" className="shrink-0 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                <CreditCard className="h-4 w-4" /> Scegli il pacchetto
              </Button>
            )}
          </div>
        )}

        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="rounded-2xl border border-white/5 bg-[#0A1124] p-5 sm:p-8">
              <p className="mb-6 text-xs uppercase tracking-[0.25em] text-white/40">Percorso del libro</p>
              <Timeline states={states} current={book.status} onSelect={isAdmin ? changeStatus : undefined} lockedStates={(isAdmin || book.package) ? [] : gateStates} />
              {isAdmin && <p className="mt-2 text-xs text-white/30">Clicca uno stato per aggiornarlo (team). Il gate pagamento è bypassato per il team.</p>}
            </div>
          </div>

          {/* Tabs column */}
          <div className="min-w-0 lg:col-span-8">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="flex h-auto w-full flex-nowrap justify-start gap-1 overflow-x-auto border-b border-white/5 bg-transparent p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[
                  ["updates", "Aggiornamenti"],
                  ["chat", "Chat"],
                  ["documents", "Documenti"],
                  ["chapters", "Capitoli"],
                  ["cover", "Copertina"],
                  ["royalty", "Royalty"],
                  ["payment", "Pagamento"],
                ].map(([v, l]) => {
                  const dot = (v === "chat" && unread.chat > 0) || (v === "updates" && unread.updates > 0);
                  return (
                    <TabsTrigger
                      key={v}
                      value={v}
                      data-testid={`tab-${v}`}
                      className="relative shrink-0 rounded-none border-b-2 border-transparent px-4 py-3 text-sm text-white/50 data-[state=active]:border-[#D4AF37] data-[state=active]:bg-transparent data-[state=active]:text-white"
                    >
                      {l}
                      {dot && (
                        <span
                          data-testid={`tab-unread-dot-${v}`}
                          className="absolute right-1 top-2 h-2 w-2 rounded-full bg-[#D4AF37]"
                        />
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Updates */}
              <TabsContent value="updates" className="mt-8">
                {isAdmin && (
                  <div
                    data-testid="updates-author-last-read"
                    className={`mb-4 flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs w-fit ${
                      readByAuthor.updates_read_at
                        ? "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]"
                        : "border-white/10 bg-white/5 text-white/50"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${readByAuthor.updates_read_at ? "bg-[#D4AF37]" : "bg-white/30"}`} />
                    {readByAuthor.updates_read_at
                      ? `Autore ha visto gli aggiornamenti ${relativeTime(readByAuthor.updates_read_at)}`
                      : "Autore non ha ancora aperto gli aggiornamenti"}
                  </div>
                )}
                {updates.length === 0 ? (
                  <p className="text-white/40">Nessun aggiornamento dal team, per ora.</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {updates.map((u, i) => (
                      <motion.div
                        key={u.update_id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="rounded-xl border border-white/5 bg-[#0A1124] p-5"
                        data-testid={`update-${u.update_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-sm text-[#D4AF37]">
                            <Sparkles className="h-3.5 w-3.5" /> {u.author_name}
                          </span>
                          <div className="flex items-center gap-2">
                            {isAdmin && (() => {
                              const seen = readByAuthor.updates_read_at && new Date(u.created_at) <= new Date(readByAuthor.updates_read_at);
                              return (
                                <span
                                  title={seen ? "Letto dall'autore" : "Non ancora letto"}
                                  data-testid={`update-read-${u.update_id}-${seen ? "read" : "sent"}`}
                                  className={`text-xs ${seen ? "text-[#D4AF37]" : "text-white/30"}`}
                                >
                                  {seen ? "✓✓ letto" : "✓ inviato"}
                                </span>
                              );
                            })()}
                            <span className="text-xs text-white/30">{fmtDate(u.created_at)}</span>
                          </div>
                        </div>
                        <p className="mt-3 text-white/80">{u.message}</p>
                        {u.state && (
                          <span className="mt-3 inline-block rounded-full bg-white/5 px-3 py-1 text-xs text-white/50">
                            → {u.state}
                          </span>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Chat */}
              <TabsContent value="chat" className="mt-8">
                <div className="flex flex-col rounded-2xl border border-white/5 bg-[#0A1124]" data-testid="chat-panel">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-white/5 px-4 py-3 sm:px-5 sm:py-4">
                    <MessagesSquare className="h-4 w-4 text-[#D4AF37]" />
                    <p className="text-sm text-white">Chat con {isAdmin ? "l'autore" : "il team"}</p>
                    <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
                      {messages.length > 0 && (
                        <Button
                          onClick={exportChat}
                          data-testid="export-chat-btn"
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1.5 rounded-full px-3 text-xs text-white/60 hover:bg-white/5 hover:text-[#D4AF37]"
                        >
                          <Download className="h-3.5 w-3.5" /> Esporta PDF
                        </Button>
                      )}
                      <span className="hidden items-center gap-1.5 text-xs text-white/30 sm:flex">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> in tempo reale
                      </span>
                      {isAdmin && (
                        <span
                          data-testid="chat-author-last-read"
                          className={`flex items-center gap-1.5 text-xs ${readByAuthor.chat_read_at ? "text-[#D4AF37]" : "text-white/30"}`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${readByAuthor.chat_read_at ? "bg-[#D4AF37]" : "bg-white/20"}`} />
                          {readByAuthor.chat_read_at
                            ? `autore ha visto ${relativeTime(readByAuthor.chat_read_at)}`
                            : "autore non ha ancora aperto la chat"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex h-[420px] flex-col gap-3 overflow-y-auto p-5" data-testid="chat-messages">
                    {messages.length === 0 ? (
                      <div className="flex flex-1 flex-col items-center justify-center text-center">
                        <MessagesSquare className="h-8 w-8 text-white/20" />
                        <p className="mt-4 text-white/40">Scrivi qui per parlare direttamente {isAdmin ? "con l'autore" : "con il team"}.</p>
                        <p className="mt-1 text-xs text-white/25">Niente più WhatsApp: tutto resta legato al tuo libro.</p>
                      </div>
                    ) : (
                      messages.map((m) => {
                        const mine = m.sender_id === user?.user_id;
                        const team = m.sender_role === "admin";
                        return (
                          <div key={m.message_id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`message-${m.message_id}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                              mine
                                ? "rounded-br-sm bg-[#D4AF37] text-[#050A15]"
                                : team
                                ? "rounded-bl-sm border border-[#D4AF37]/20 bg-[#050A15] text-white"
                                : "rounded-bl-sm bg-white/5 text-white"
                            }`}>
                              {!mine && (
                                <p className={`mb-0.5 text-[11px] font-medium ${team ? "text-[#D4AF37]" : "text-white/50"}`}>
                                  {m.sender_name}{team ? " · Team" : ""}
                                </p>
                              )}
                              {m.attachment && (
                                m.attachment.is_image ? (
                                  <img
                                    src={attUrls[m.attachment.attachment_id]}
                                    alt={m.attachment.filename}
                                    onClick={() => downloadAttachment(m.attachment)}
                                    data-testid={`attachment-image-${m.attachment.attachment_id}`}
                                    className={`mb-2 max-h-52 cursor-pointer rounded-lg object-cover ${attUrls[m.attachment.attachment_id] ? "" : "min-h-24 w-40 animate-pulse bg-white/10"}`}
                                  />
                                ) : (
                                  <button
                                    onClick={() => downloadAttachment(m.attachment)}
                                    data-testid={`attachment-file-${m.attachment.attachment_id}`}
                                    className={`mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-left text-xs ${mine ? "bg-[#050A15]/20" : "bg-white/5"}`}
                                  >
                                    <FileText className="h-4 w-4 shrink-0" />
                                    <span className="truncate">{m.attachment.filename}</span>
                                    <Download className="h-3.5 w-3.5 shrink-0 opacity-70" />
                                  </button>
                                )
                              )}
                              {m.text && <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>}
                            </div>
                            <span className="mt-1 flex items-center gap-1 px-1 text-[10px] text-white/25">
                              {fmtDate(m.created_at)}
                              {mine && isAdmin && (() => {
                                const seen = readByAuthor.chat_read_at && new Date(m.created_at) <= new Date(readByAuthor.chat_read_at);
                                return (
                                  <span
                                    title={seen ? `Letto dall'autore` : "Consegnato"}
                                    data-testid={`msg-read-${m.message_id}-${seen ? "read" : "sent"}`}
                                    className={seen ? "text-[#D4AF37]" : "text-white/30"}
                                  >
                                    {seen ? "✓✓" : "✓"}
                                  </span>
                                );
                              })()}
                            </span>
                          </div>
                        );
                      })
                    )}
                    <div ref={chatEndRef} />
                  </div>
                  <form onSubmit={sendChatMessage} className="flex items-center gap-2 border-t border-white/5 p-4">
                    <input ref={chatFileRef} type="file" onChange={uploadChatAttachment} className="hidden" data-testid="chat-file-input" />
                    <Button
                      type="button"
                      onClick={() => chatFileRef.current?.click()}
                      disabled={uploadingAtt}
                      data-testid="chat-attach-btn"
                      size="icon"
                      variant="ghost"
                      className="h-10 w-10 shrink-0 rounded-full text-white/50 hover:bg-white/5 hover:text-[#D4AF37]"
                    >
                      {uploadingAtt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    </Button>
                    <input
                      data-testid="chat-input"
                      value={msgInput}
                      onChange={(e) => setMsgInput(e.target.value)}
                      placeholder="Scrivi un messaggio…"
                      className="flex-1 rounded-full border border-white/10 bg-[#050A15] px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37]/50 focus:outline-none"
                    />
                    <Button type="submit" disabled={sending || !msgInput.trim()} data-testid="chat-send-btn" size="icon" className="h-10 w-10 shrink-0 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158] disabled:opacity-40">
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                </div>
              </TabsContent>

              {/* Documents */}
              <TabsContent value="documents" className="mt-8">
                <input ref={fileRef} type="file" onChange={uploadFile} className="hidden" data-testid="document-file-input" />
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <span className="text-xs uppercase tracking-widest text-white/40">Cartella</span>
                  <Select value={docFolder} onValueChange={setDocFolder}>
                    <SelectTrigger data-testid="doc-folder-select" className="w-52 border-white/10 bg-white/5 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0A1124] text-white">
                      <SelectItem value="copertine" data-testid="doc-folder-copertine" className="focus:bg-white/5 focus:text-white">Copertine</SelectItem>
                      <SelectItem value="capitoli" data-testid="doc-folder-capitoli" className="focus:bg-white/5 focus:text-white">Bozze capitoli</SelectItem>
                      <SelectItem value="altro" data-testid="doc-folder-altro" className="focus:bg-white/5 focus:text-white">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="upload-document-btn"
                  className="flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#0A1124] py-12 transition-colors hover:border-[#D4AF37]/40"
                >
                  <Upload className="h-6 w-6 text-[#D4AF37]" />
                  <p className="mt-3 text-white">Carica un file in "{DOC_FOLDER_LABELS[docFolder]}"</p>
                  <p className="mt-1 text-xs text-white/30">Copertine, bozze capitoli, manoscritti (max 15MB) · richiede doppia approvazione tua e {isAdmin ? "del cliente" : "del team"}</p>
                </button>
                {["copertine", "capitoli", "altro"].map((folderKey) => {
                  const folderDocs = docs.filter((d) => (d.folder || "altro") === folderKey);
                  if (folderDocs.length === 0) return null;
                  return (
                    <div key={folderKey} data-testid={`doc-folder-group-${folderKey}`}>
                      <p className="mb-2 mt-6 text-xs uppercase tracking-[0.2em] text-[#D4AF37]/70">{DOC_FOLDER_LABELS[folderKey]}</p>
                      <div className="flex flex-col gap-2">
                        {folderDocs.map((d) => {
                          const meApproved = isAdmin ? d.admin_approved : d.author_approved;
                          const otherApproved = isAdmin ? d.author_approved : d.admin_approved;
                          const badge = d.feedback && !d.author_approved && !d.admin_approved
                            ? { text: "Modifiche richieste", cls: "border-orange-400/30 bg-orange-400/10 text-orange-300" }
                            : d.author_approved && d.admin_approved
                              ? { text: "Approvato da entrambi", cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" }
                              : meApproved
                                ? { text: `Approvato da te · in attesa ${isAdmin ? "del cliente" : "del team"}`, cls: "border-white/10 bg-white/5 text-white/60" }
                                : otherApproved
                                  ? { text: "Tocca a te approvare", cls: "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]" }
                                  : { text: "In attesa di approvazione", cls: "border-white/10 bg-white/5 text-white/50" };
                          return (
                            <div key={d.doc_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-[#0A1124] px-5 py-4" data-testid={`document-${d.doc_id}`}>
                              <div className="flex min-w-0 flex-1 basis-48 items-center gap-3">
                                <FileText className="h-5 w-5 shrink-0 text-white/40" />
                                <div className="min-w-0">
                                  <p className="truncate text-sm text-white">{d.filename}</p>
                                  <p className="text-xs text-white/30">{(d.size / 1024).toFixed(0)} KB · {fmtDate(d.uploaded_at)}</p>
                                </div>
                              </div>
                              <span className={`rounded-full border px-2.5 py-1 text-[11px] ${badge.cls}`} data-testid={`doc-status-${d.doc_id}`}>
                                {badge.text}
                              </span>
                              <div className="ml-auto flex flex-wrap items-center gap-1">
                                {!meApproved && (
                                  <Button size="sm" variant="outline" onClick={() => approveDoc(d.doc_id)} data-testid={`doc-approve-${d.doc_id}`}
                                    className="h-8 gap-1.5 rounded-full border-[#D4AF37]/30 bg-transparent px-3 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/10">
                                    <Check className="h-3.5 w-3.5" /> Approva
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => { setDocFeedbackFor(d.doc_id); setDocFeedbackText(""); }} data-testid={`doc-feedback-${d.doc_id}`}
                                  className="h-8 gap-1.5 rounded-full border-white/10 bg-transparent px-3 text-xs text-white/60 hover:bg-white/5 hover:text-white">
                                  <MessageSquare className="h-3.5 w-3.5" /> Modifiche
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => downloadDoc(d.doc_id, d.filename)} data-testid={`download-${d.doc_id}`} className="text-white/50 hover:bg-white/5 hover:text-white">
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => deleteDoc(d.doc_id)} data-testid={`delete-doc-${d.doc_id}`} className="text-white/50 hover:bg-white/5 hover:text-red-400">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              {d.feedback && (
                                <p className="w-full text-xs italic text-orange-300/80" data-testid={`doc-feedback-text-${d.doc_id}`}>
                                  "{d.feedback}"
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>

              {/* Chapters */}
              <TabsContent value="chapters" className="mt-8">
                {book.questionnaire_completed && (
                  <div className="mb-6 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-6" data-testid="ai-draft-card">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        <Wand2 className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" />
                        <div>
                          <p className="font-serif text-lg text-white">Genera con AI</p>
                          <p className="text-sm text-white/50">
                            Crea la struttura del libro e le bozze dei primi capitoli dalle risposte del questionario.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={generateDraft}
                        disabled={aiBusy}
                        data-testid="generate-ai-draft-btn"
                        className="shrink-0 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"
                      >
                        {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        {aiBusy ? "Generazione…" : book.ai_outline?.length ? "Rigenera" : "Genera struttura"}
                      </Button>
                    </div>
                    {book.ai_synopsis && (
                      <div className="mt-5 rounded-lg border border-white/5 bg-[#050A15] p-4">
                        <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Sinossi proposta</p>
                        <p className="mt-2 text-sm italic leading-relaxed text-white/70">{book.ai_synopsis}</p>
                      </div>
                    )}
                    {book.ai_outline?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs uppercase tracking-widest text-white/40">Struttura proposta</p>
                        <div className="mt-3 flex flex-col gap-2">
                          {book.ai_outline.map((o) => (
                            <div key={o.number} className="flex gap-3 rounded-lg bg-[#050A15]/60 px-3 py-2" data-testid={`ai-outline-${o.number}`}>
                              <span className="font-mono text-xs text-[#D4AF37]">{String(o.number).padStart(2, "0")}</span>
                              <div>
                                <p className="text-sm text-white">{o.title}</p>
                                {o.summary && <p className="text-xs text-white/40">{o.summary}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {chapters.length === 0 ? (
                  <p className="text-white/40">I capitoli appariranno qui non appena il team inizierà la scrittura.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {chapters.map((c) => {
                      const s = chapterStatusLabel[c.status] || chapterStatusLabel.pending_approval;
                      return (
                        <div key={c.chapter_id} className="rounded-xl border border-white/5 bg-[#0A1124] p-5" data-testid={`chapter-${c.chapter_id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="font-mono text-xs text-[#D4AF37]">Capitolo {c.number}</p>
                              <p className="mt-1 font-serif text-xl text-white">{c.title}</p>
                            </div>
                            <span className={`rounded-full border px-3 py-1 text-xs ${s.c}`}>{s.t}</span>
                          </div>
                          <p className="mt-3 text-sm text-white/50 line-clamp-2">{c.content}</p>
                          {c.feedback && <p className="mt-2 text-xs text-orange-400/80">Feedback: {c.feedback}</p>}
                          {c.status !== "approved" && (
                            <div className="mt-4 flex gap-2">
                              <Button size="sm" onClick={() => approveChapter(c.chapter_id)} data-testid={`approve-chapter-${c.chapter_id}`} className="gap-1 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                                <Check className="h-3.5 w-3.5" /> Approva
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setFeedbackFor(c.chapter_id)} data-testid={`request-changes-${c.chapter_id}`} className="gap-1 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
                                <MessageSquare className="h-3.5 w-3.5" /> Richiedi modifiche
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {!isAdmin && !book.package && chapters.length > 0 && chapters.every((c) => c.status === "approved") && (
                  <div className="mt-6 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6 text-center" data-testid="accept-revision-card">
                    <p className="font-serif text-xl text-white">Hai approvato tutti i capitoli.</p>
                    <p className="mt-1 text-sm text-white/50">Accetta la revisione e scegli il pacchetto per avviare la creazione del tuo libro.</p>
                    <Button onClick={() => setActiveTab("payment")} data-testid="accept-revision-btn" className="mt-5 gap-2 rounded-full bg-[#D4AF37] px-6 text-[#050A15] hover:bg-[#E5C158]">
                      <CreditCard className="h-4 w-4" /> Accetta la revisione e procedi al pagamento
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* Cover */}
              <TabsContent value="cover" className="mt-8">
                <p className="mb-6 flex items-center gap-2 text-sm text-white/50">
                  <ImageIcon className="h-4 w-4 text-[#D4AF37]" /> Scegli la copertina che rappresenta il tuo libro.
                </p>
                {isAdmin && (
                  <div className="mb-6 rounded-xl border border-white/5 bg-[#0A1124] p-5" data-testid="cover-upload-card">
                    <p className="mb-3 text-xs uppercase tracking-[0.2em] text-white/40">Carica una copertina (team)</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <input ref={coverFileRef} type="file" accept="image/*" className="hidden" data-testid="cover-file-input"
                        onChange={(e) => setCoverFile(e.target.files?.[0] || null)} />
                      <Button type="button" variant="outline" onClick={() => coverFileRef.current?.click()} data-testid="cover-file-btn"
                        className="gap-2 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5 hover:text-white">
                        <Upload className="h-4 w-4" /> {coverFile ? coverFile.name : "Scegli immagine"}
                      </Button>
                      <Input value={coverLabel} onChange={(e) => setCoverLabel(e.target.value)} data-testid="cover-label-input"
                        placeholder="Etichetta (es. Bozza A)"
                        className="w-56 border-white/10 bg-white/5 text-white placeholder:text-white/30" />
                      <Button onClick={uploadCover} disabled={coverBusy} data-testid="cover-upload-btn"
                        className="gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                        <Plus className="h-4 w-4" /> {coverBusy ? "Caricamento…" : "Aggiungi proposta"}
                      </Button>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {covers.map((c) => {
                    const selected = book.selected_cover === c.image_url;
                    const isUploaded = c.image_url?.startsWith("cover-file:");
                    return (
                      <div key={c.cover_id} className="group relative">
                        <button
                          onClick={() => selectCover(c.image_url)}
                          data-testid={`cover-option-${c.cover_id}`}
                          className={`relative w-full overflow-hidden rounded-xl border-2 transition-all ${
                            selected ? "border-[#D4AF37]" : "border-transparent hover:border-white/20"
                          }`}
                        >
                          <img src={coverSrc(c.image_url)} alt={c.label} className="aspect-[3/4] w-full object-cover" />
                          {selected && (
                            <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#D4AF37] text-[#050A15]">
                              <Check className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </button>
                        <p className="mt-2 truncate text-center text-xs text-white/50">{c.label}</p>
                        {isAdmin && isUploaded && (
                          <Button variant="ghost" size="icon" onClick={() => deleteCover(c.cover_id)} data-testid={`cover-delete-${c.cover_id}`}
                            className="absolute left-2 top-2 h-8 w-8 rounded-full bg-[#050A15]/80 text-red-300 opacity-90 hover:bg-red-400/20 hover:text-red-300"
                            title="Rimuovi questa proposta">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              {/* Royalty */}
              <TabsContent value="royalty" className="mt-8">
                <RoyaltySection
                  book={book}
                  royalty={royalty}
                  isAdmin={isAdmin}
                  onRefresh={async () => {
                    const { data } = await api.get(`/books/${bookId}/royalties`);
                    setRoyalty(data);
                  }}
                />
              </TabsContent>

              {/* Payment */}
              <TabsContent value="payment" className="mt-8">
                {book.package ? (
                  <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6" data-testid="package-active">
                    <p className="flex items-center gap-2 text-[#D4AF37]"><Check className="h-4 w-4" /> Pacchetto attivo</p>
                    <p className="mt-2 font-serif text-2xl text-white">
                      {packages.find((p) => p.lookup_key === book.package)?.name || book.package}
                    </p>
                  </div>
                ) : (
                  <>
                    {recommendation?.completed && recommendation?.package && (
                      <div className="mb-6 overflow-hidden rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-b from-[#D4AF37]/10 to-transparent p-6" data-testid="smart-quote-card">
                        <div className="flex items-center gap-2">
                          <Sparkle className="h-4 w-4 text-[#D4AF37]" />
                          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Preventivo Smart · consigliato per te</p>
                        </div>
                        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                          <div>
                            <p className="font-serif text-3xl font-light text-white">{recommendation.package.name}</p>
                            <p className="font-serif text-4xl font-light text-[#D4AF37]">€{recommendation.package.price}</p>
                          </div>
                          <Button
                            onClick={() => checkout(recommendation.recommended)}
                            data-testid="checkout-recommended-btn"
                            className="gap-2 rounded-full bg-[#D4AF37] px-6 text-[#050A15] hover:bg-[#E5C158]"
                          >
                            <CreditCard className="h-4 w-4" /> Acquista il consigliato
                          </Button>
                        </div>
                        <div className="mt-5 border-t border-white/10 pt-4">
                          <p className="text-xs uppercase tracking-widest text-white/40">Perché te lo consigliamo</p>
                          <ul className="mt-3 flex flex-col gap-2">
                            {recommendation.reasons.map((r, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-white/70" data-testid={`quote-reason-${i}`}>
                                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D4AF37]" /> {r}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {visiblePackages.length > 1 && (
                      <p className="mb-4 text-sm text-white/40">Oppure scegli tu il pacchetto che preferisci:</p>
                    )}
                    <div className={`grid gap-4 ${visiblePackages.length > 1 ? "sm:grid-cols-2" : "sm:max-w-md"}`}>
                      {visiblePackages.map((p) => {
                        const recommended = recommendation?.recommended === p.lookup_key;
                        return (
                          <div key={p.lookup_key} className={`flex flex-col rounded-xl border p-6 ${recommended ? "border-[#D4AF37]/40 bg-[#111A30]" : "border-white/5 bg-[#0A1124]"}`} data-testid={`package-${p.lookup_key}`}>
                            {recommended && <span className="mb-2 w-fit rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#D4AF37]">Consigliato</span>}
                            <p className="font-serif text-xl text-white">{p.name}</p>
                            <p className="mt-2 font-serif text-3xl font-light text-[#D4AF37]">€{p.price}</p>
                            <ul className="mt-4 flex flex-1 flex-col gap-2">
                              {p.features.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-xs text-white/50">
                                  <Check className="mt-0.5 h-3 w-3 shrink-0 text-[#D4AF37]" /> {f}
                                </li>
                              ))}
                            </ul>
                            <Button
                              onClick={() => checkout(p.lookup_key)}
                              data-testid={`checkout-${p.lookup_key}`}
                              className={`mt-6 gap-2 rounded-full ${recommended ? "bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]" : "bg-white/5 text-white hover:bg-white/10"}`}
                            >
                              <CreditCard className="h-4 w-4" /> Acquista
                            </Button>
                          </div>
                        );
                      })}
                    </div>

                    {visiblePackages.length > 1 && (
                    <div className="mt-8">
                      <button
                        onClick={() => setShowCompare((s) => !s)}
                        data-testid="toggle-compare-btn"
                        className="text-sm text-[#D4AF37] underline-offset-4 hover:underline"
                      >
                        {showCompare ? "Nascondi confronto" : "Confronta i pacchetti fianco a fianco"}
                      </button>
                      {showCompare && (
                        <div className="mt-4 overflow-x-auto rounded-2xl border border-white/5 bg-[#0A1124]" data-testid="compare-table">
                          <table className="w-full min-w-[420px] text-left text-sm">
                            <thead>
                              <tr className="border-b border-white/5">
                                <th className="p-4 font-normal text-white/40">Cosa include</th>
                                {COMPARE_KEYS.map((k) => {
                                  const p = packages.find((x) => x.lookup_key === k);
                                  const rec = recommendation?.recommended === k;
                                  return (
                                    <th key={k} className="p-4 text-center">
                                      <span className="font-serif text-base text-white">{p?.name?.replace("SelfBook ", "")}</span>
                                      {rec && <span className="ml-1 text-[10px] uppercase text-[#D4AF37]">★</span>}
                                      <p className="mt-1 font-serif text-lg font-light text-[#D4AF37]">€{p?.price}</p>
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {COMPARE_ROWS.map((row) => (
                                <tr key={row.label} className="border-b border-white/5 last:border-0">
                                  <td className="p-4 text-white/70">{row.label}</td>
                                  {row.vals.map((v, i) => (
                                    <td key={i} className="p-4 text-center">
                                      {v ? (
                                        <Check className="mx-auto h-4 w-4 text-[#D4AF37]" />
                                      ) : (
                                        <span className="text-white/20">—</span>
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Feedback dialog */}
      <Dialog open={!!feedbackFor} onOpenChange={(o) => !o && setFeedbackFor(null)}>
        <DialogContent className="border-white/10 bg-[#0A1124] text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Richiedi modifiche</DialogTitle>
          </DialogHeader>
          <Textarea
            data-testid="feedback-textarea"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Cosa vorresti cambiare in questo capitolo?"
            rows={4}
            className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
          />
          <DialogFooter>
            <Button onClick={submitFeedback} data-testid="submit-feedback-btn" className="rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
              Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document feedback dialog */}
      <Dialog open={!!docFeedbackFor} onOpenChange={(o) => !o && setDocFeedbackFor(null)}>
        <DialogContent className="border-white/10 bg-[#0A1124] text-white" data-testid="doc-feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Richiedi modifiche sul file</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-white/40">Le approvazioni già date verranno azzerate e il file tornerà "In attesa di approvazione".</p>
          <Textarea
            data-testid="doc-feedback-textarea"
            value={docFeedbackText}
            onChange={(e) => setDocFeedbackText(e.target.value)}
            placeholder="Cosa va modificato in questo file?"
            rows={4}
            className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
          />
          <DialogFooter>
            <Button onClick={submitDocFeedback} data-testid="doc-feedback-submit" className="rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
              Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: mark book as paid dialog */}
      <Dialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
        <DialogContent className="border-white/10 bg-[#0A1124] text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Segna il libro come pagato</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-white/60">
              Questa azione attiva il libro come se l'autore avesse pagato: sblocca la produzione,
              porta lo stato a <span className="text-white">Copertina</span> e invia l'email
              "Produzione avviata" all'autore. Sarà registrata una transazione con nota "admin".
            </p>
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-widest text-white/40">Pacchetto</label>
              <div className="grid gap-2 sm:grid-cols-2">
                {[
                  { key: "essential_onetime", name: "Essential (prezzo di lancio)", price: "€ 199" },
                  { key: "professional_onetime", name: "Professional", price: "€ 497" },
                ].map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setMarkPaidPkg(p.key)}
                    data-testid={`admin-mark-paid-choice-${p.key}`}
                    className={`rounded-xl border p-4 text-left transition-all ${
                      markPaidPkg === p.key
                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-white/10 bg-white/[0.02] hover:border-white/20"
                    }`}
                  >
                    <p className="font-serif text-lg text-white">{p.name}</p>
                    <p className="text-sm text-[#D4AF37]">{p.price}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMarkPaidOpen(false)}
              className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5"
            >
              Annulla
            </Button>
            <Button
              onClick={markPaid}
              disabled={markPaidBusy}
              data-testid="admin-mark-paid-confirm-btn"
              className="gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"
            >
              <ShieldCheck className="h-4 w-4" />
              {markPaidBusy ? "Attivazione…" : "Attiva il libro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
